import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import copy from 'copy-to-clipboard';
import { THIRD_PARTY_VEHICLE } from 'data-layer-cr/dist/constants/products';
import {
  PENDING_EMISSION,
  PENDING_INSPECTION,
} from 'data-layer-cr/dist/constants/quotationStatus';
import { Quotation } from 'data-layer-cr/dist/models';
import { DefaultButton } from 'design-system';
import React from 'react';
import { Copy } from 'react-feather';

import { isNotComplete } from '../../../../../functions/productMethods';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    copyLinkBtn: {
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      [theme.breakpoints.down('xs')]: {
        width: theme.spacing(18.75),
      },
    },
    text: {
      marginLeft: theme.spacing(1),
    },
  }),
);

type Props = {
  disabled?: boolean;
  emissionLink?: boolean;
  inspectionLink?: boolean;
  text?: React.ReactNode;
  quotation: Quotation;
  id?: string;
};

const messageByCustomerType = (customerType: string): string =>
  customerType === 'N'
    ? '- Cédula o pasaporte'
    : `- No. de RUC de la empresa
- Datos del beneficiario final`;
/**
 * Full message for inspections
 */
export const fullMessage = `
Recuerda que debes realizar la Inspección desde tu celular y que necesitas:  
\r\n
- Estar cerca del auto
- Tener el auto en un sitio con buena iluminación 
- Tener a la mano tu registro vehicular 
\r\n
Puedes realizar la inspección desde este link:
`;

/**
 * Full message for third party emissions.
 */
export const fullThirdPartyDamagesMessage = (customerType: string): string => `
Recuerda que para comprar tu póliza necesitas:
\r\n
${messageByCustomerType(customerType)}
- Registro vehicular
\r\n
Puedes realizar la emisión desde este enlace:
`;

/**
 * The message to copy when a full coverage quotation is in the pending emission
 * step.
 */
export const fullEmissionMessage = (customerType: string): string => `
Recuerda que para comprar tu póliza necesitas:
\r\n
${messageByCustomerType(customerType)}
\r\n
Puedes realizar la emisión desde este enlace:
`;

export const inspectionButtonContent = 'Copiar link de inspección';
export const emissionButtonContent = 'Emitir póliza';

/**
 * Returns the text content that will be rendered on the button IF no text
 * parameter was specified via props.
 */
function getButtonContent(
  text: React.ReactNode,
  inspectionLink: boolean,
  emissionLink: boolean,
  quotation: Quotation,
  generateContent: (content: string) => JSX.Element,
): React.ReactNode | void {
  if (text !== '') {
    return text;
  }
  if (emissionLink) {
    return generateContent('COPIAR LINK DE EMISIÓN');
  }
  if (inspectionLink) {
    return generateContent(inspectionButtonContent);
  }
  if (
    quotation.status.id !== PENDING_INSPECTION ||
    quotation.productId === THIRD_PARTY_VEHICLE
  ) {
    return emissionButtonContent;
  }
  return generateContent(inspectionButtonContent);
}

const CopyInspectionLinkButton = ({
  quotation,
  text = '',
  disabled = false,
  id = '',
  inspectionLink = false,
  emissionLink = false,
}: Props): JSX.Element => {
  const classes = useStyles();
  const base = process.env.REACT_APP_INSPECTION_URL;
  const url = `${base}${quotation.urlSalt}`;
  /**
   * Builds additional data that must be shown before the actual quotation link,
   * depending on the quotation status and the product.
   */
  function getMessageDecoration(): string {
    if (isNotComplete(quotation.productId)) {
      return fullThirdPartyDamagesMessage(quotation.customerType);
    }
    if (quotation.status.id === PENDING_INSPECTION) {
      return fullMessage;
    }
    if (quotation.status.id === PENDING_EMISSION) {
      return fullEmissionMessage(quotation.customerType);
    }
    return '';
  }

  /**
   * Copy the generated message to the clipboard
   */
  function copyToClipboard(): void {
    copy(`${getMessageDecoration()}${url}`);
  }

  const copyLinkContent = (displayedText: string): JSX.Element => (
    <div className={classes.copyLinkBtn} data-testid="copy-link-content">
      <Copy width={24} height={24} />
      <div className={classes.text}>{displayedText}</div>
    </div>
  );

  return (
    <DefaultButton
      id={id}
      disabled={disabled}
      fullWidth
      variant="borderless"
      onClick={copyToClipboard}
    >
      <>
        {getButtonContent(
          text,
          inspectionLink,
          emissionLink,
          quotation,
          copyLinkContent,
        )}
        <span hidden id={`${id}-hidden`}>
          {url}
        </span>
      </>
    </DefaultButton>
  );
};

export default CopyInspectionLinkButton;
